import {
  FormLabel,
  Button,
  Popover,
  Divider,
  InputAdornment,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import React, { useState, useMemo, forwardRef, useCallback, useEffect } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { StyledTextfield } from "src/reusable-components/QuickFilter/styles";
import { PeriodQuickFilter, Props, State } from "./interfaces";

import { FromToContainer, FromTo } from "./styles";
import "react-datepicker/dist/react-datepicker.css";
import { convertDateToUTC, formatDateToMUIFormat } from "../../../utils/time-utils";
import { endOfDay } from "date-fns";
import { isEqual } from "lodash";

export const PeriodFromToQuickfilter = <Q extends PeriodQuickFilter>({
  quickFilters,
  handleChangeQuickFilters,
}: Props<Q>) => {
  const { t, i18n } = useTranslation();
  const tRP = "components.quickfilters.period_from_to";
  const TODAY = useMemo(() => {
    const today = new Date();

    return {
      thisDate: today.getDate(),
      thisYear: today.getFullYear(),
      thisMonth: today.getMonth(),
    };
  }, []);

  const [state, setState] = useState<State>({
    menuOpen: false,
    anchorEl: null,
    customPeriod: {
      ...(quickFilters.period
        ? {
            startDate: new Date(
              Date.UTC(
                new Date(quickFilters.period.startDate).getUTCFullYear(),
                new Date(quickFilters.period.startDate).getUTCMonth(),
                new Date(quickFilters.period.startDate).getUTCDate(),
                0,
                0
              )
            ),
            endDate: new Date(
              Date.UTC(
                new Date(quickFilters.period.endDate).getUTCFullYear(),
                new Date(quickFilters.period.endDate).getUTCMonth(),
                new Date(quickFilters.period.endDate).getUTCDate()
              )
            ),
            year: quickFilters.period.year,
          }
        : {
            startDate: new Date(),
            endDate: new Date(),
            year: TODAY.thisYear,
          }),
    },
    inputValue: "",
    anyPeriod: true,
  });

  const getCurrentLanguage = (): string => (i18n.language === "en" ? "enGB" : i18n.language);

  const CustomInput = forwardRef<
    HTMLButtonElement,
    { value: string; onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void }
  >(({ value, onClick }, ref) => (
    <Button
      variant="outlined"
      onClick={onClick}
      ref={ref}
      style={{ fontSize: "13px" }}
    >
      {value}
    </Button>
  ));
  CustomInput.displayName = "CustomInput";

  const getLabel = useCallback((): string => {
    if (state.anyPeriod) {
      return `${t(`${tRP}.any_period`)}`;
    } else {
      const sd = new Date(
        Date.UTC(
          state.customPeriod.startDate.getUTCFullYear(),
          state.customPeriod.startDate.getUTCMonth(),
          state.customPeriod.startDate.getUTCDate(),
          state.customPeriod.startDate.getUTCHours()
        )
      );

      const ed = new Date(
        Date.UTC(
          state.customPeriod.endDate.getUTCFullYear(),
          state.customPeriod.endDate.getUTCMonth(),
          state.customPeriod.endDate.getUTCDate(),
          state.customPeriod.endDate.getUTCHours()
        )
      );
      return `${t("common_words.from")} : ${formatDateToMUIFormat(sd)} \n ${t(
        "common_words.to"
      )} : ${formatDateToMUIFormat(ed)}`;
    }
  }, [t, state.anyPeriod, state.customPeriod]);

  const handleChangeCustomPeriodCallback = useCallback(() => {
    if (!quickFilters.period) {
      handleChangeQuickFilters("period", null);
    } else {
      !isEqual(quickFilters.period, state.customPeriod) && handleChangeQuickFilters("period", state.customPeriod);
    }
  }, [handleChangeQuickFilters, quickFilters.period, state.customPeriod]);

  const handleChangeOption = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setState((state) => ({
        ...state,
        anyPeriod: event.currentTarget.value === "anyPeriod",
      }));

      handleChangeQuickFilters("period", event.currentTarget.value === "anyPeriod" ? null : state.customPeriod);
    },
    [state.customPeriod]
  );

  useEffect(() => {
    handleChangeCustomPeriodCallback();
  }, [state.customPeriod]);

  return (
    <>
      <StyledTextfield
        disabled={true}
        // label={"Period"}
        // InputLabelProps={{
        //     shrink: true,
        //   }}
        InputProps={{
          disabled: false,
          readOnly: true,
          style: { cursor: "pointer", textOverflow: "ellipsis" },
          spellCheck: false,

          inputProps: {
            style: { cursor: "pointer", textOverflow: "ellipsis" },
          },

          startAdornment: (
            <InputAdornment
              position={"start"}
              style={{ display: "flex", height: "100%", gap: "5px" }}
            >
              <Typography>{`${t(`${tRP}.shrinked_label`)}`}</Typography>
              <Divider
                orientation={"vertical"}
                flexItem
              />
            </InputAdornment>
          ),
        }}
        multiline
        maxRows={2}
        onClick={(event) => {
          setState((state) => ({
            ...state,
            menuOpen: true,
            anchorEl: event.currentTarget,
          }));
        }}
        value={getLabel()}
      />

      <Popover
        open={state.menuOpen}
        anchorEl={state.anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            padding: "10px",
            width: "250px",
            overflow: "visible",
          },
        }}
        onClose={() => {
          setState((state) => ({ ...state, menuOpen: false }));
        }}
        keepMounted={true}
      >
        <RadioGroup
          value={state.anyPeriod ? "anyPeriod" : "period"}
          onChange={handleChangeOption}
        >
          <FormControlLabel
            value="anyPeriod"
            control={<Radio />}
            label="Any period"
          />
          <FormControlLabel
            value="period"
            control={<Radio />}
            label="From ... to "
          />
        </RadioGroup>
        <FromToContainer style={{ display: "block" }}>
          <FromTo>
            <FormLabel>{t("common_words.from")}</FormLabel>
            <DatePicker
              selected={
                new Date(
                  state.customPeriod.startDate.getUTCFullYear(),
                  state.customPeriod.startDate.getUTCMonth(),
                  state.customPeriod.startDate.getUTCDate()
                )
              }
              onChange={(value) => {
                !!value &&
                  value?.getTime() < state.customPeriod.endDate.getTime() &&
                  setState((state) => ({
                    ...state,
                    customPeriod: {
                      ...state.customPeriod,
                      startDate: convertDateToUTC(value),
                    },
                  }));
              }}
              customInput={React.createElement(CustomInput)}
              dateFormat="yyyy MMMM dd"
              locale={getCurrentLanguage()}
              showYearDropdown
              dropdownMode="select"
              popperPlacement="top-start"
            />
          </FromTo>

          <FromTo>
            <FormLabel>{t("common_words.to")}</FormLabel>
            <DatePicker
              selected={
                new Date(
                  state.customPeriod.endDate.getUTCFullYear(),
                  state.customPeriod.endDate.getUTCMonth(),
                  state.customPeriod.endDate.getUTCDate()
                )
              }
              onChange={(value) => {
                !!value &&
                  value?.getTime() > state.customPeriod.startDate.getTime() &&
                  setState((state) => ({
                    ...state,
                    customPeriod: {
                      ...state.customPeriod,
                      endDate: convertDateToUTC(endOfDay(value)),
                    },
                  }));
              }}
              customInput={React.createElement(CustomInput)}
              dateFormat="yyyy MMMM dd"
              locale={getCurrentLanguage()}
              showYearDropdown
              dropdownMode="select"
              popperPlacement="top"
            />
          </FromTo>
        </FromToContainer>
      </Popover>
    </>
  );
};
